/**
 * Server-renderable shell for the My Favotrip header entry.
 *
 * Bug 2 fix (Jeanne 2026-05-19): the previous `AccountHeaderLink`
 * Client Component called `usePathname()` at the top of the function.
 * That hook returns null / requires runtime navigation context, and
 * Next.js App Router static-generation of marketing-layout pages
 * (no `dynamic = "force-dynamic"`) silently drops the Client Component
 * from the SSR HTML when its hooks can't initialize. The result:
 * anonymous visitors loaded `/` and saw NO Inloggen pill in the
 * rendered HTML — so they could not discover the portal at all.
 *
 * Pattern (Jeanne 2026-05-20 approved, option 3):
 *   - This file is a pure-render component with NO hooks. It always
 *     emits the anonymous "Inloggen" link, suitable for the
 *     marketing-layout static pre-render.
 *   - `AccountHeaderLinkAuth` (sibling Client Component) hydrates the
 *     authed state via /customer/me on the client and visually swaps
 *     this shell out via CSS when the customer is logged in.
 *
 * Constraint: this component MUST NOT call any navigation hook
 * (`usePathname`, `useRouter`, `useSearchParams`) or any client-only
 * API (window, document). Keep it cheap and static.
 */

import Link from "next/link";
import { getTranslations } from "@/lib/i18n";
import { localizeHref } from "@/lib/slug-paths";
import { safeLuxeReturnTo } from "@/lib/luxe/luxe-return-to";

function ProfileIcon() {
  // Anonymous profile icon (outline). The authed-state filled variant
  // lives in AccountHeaderLinkAuth.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export default function AccountHeaderLinkAnonShell({
  variant = "desktop",
  locale = "nl",
  returnTo = null,
  aanmeldHref = null,
}) {
  // getTranslations is a pure lookup (no React hooks), so it stays safe to
  // call in this hooks-free SSR shell. Locale is threaded in from the parent
  // (Navbar reads it from the site context) so non-NL markets show a native
  // login label instead of the Dutch "Inloggen".
  const { t } = getTranslations(locale);
  // `returnTo`: alleen op /exclusive/* en /luxe/* gezet door Navbar
  // (exclusiveAccountReturnTo, dezelfde validatie als de ledenknop). Hier
  // nog eens door safeLuxeReturnTo, zodat dit nooit een open redirect wordt.
  // `aanmeldHref`: op de Duitse Exclusive-pagina's het Duitse aanmeldblok op
  // dezelfde pagina (luxeDeInlogHref), nooit de Nederlandse Exclusive-login.
  const loginHref = aanmeldHref ?? `${localizeHref("/account/login", locale)}${
    returnTo ? `?returnTo=${encodeURIComponent(safeLuxeReturnTo(returnTo))}` : ""
  }`;
  if (variant === "mobile") {
    return (
      <Link
        href={loginHref}
        aria-label={t("nav.login_aria", "Inloggen op My Favotrip")}
        className="account-header-link-shell account-header-link-shell--mobile p-2 text-gray-500 transition-colors hover:text-blue-600"
        data-account-header-state="anon"
      >
        <ProfileIcon />
      </Link>
    );
  }

  return (
    <Link
      href={loginHref}
      className="account-header-link-shell account-header-link-shell--desktop inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-2 text-[14px] font-semibold text-gray-700 transition-colors hover:border-blue-200 hover:text-blue-600"
      data-account-header-state="anon"
    >
      <ProfileIcon />
      <span className="max-w-[140px] truncate">{t("nav.login", "Inloggen")}</span>
    </Link>
  );
}
